import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, MessageCircle, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

/**
 * TradeChatDialog — a lightweight one-to-one chat pinned to a specific
 * trade request. Both participants (sender + recipient) can post messages
 * to coordinate meet-up times, platforms, epic IDs, etc.
 *
 * Props:
 *   trade    — the trade object being discussed
 *   userId   — current user's id
 *   onClose  — callback to close the dialog
 */
export default function TradeChatDialog({ trade, userId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const scrollerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!trade?.id) return;
    try {
      const list = await api.listTradeMessages(trade.id);
      setMessages(Array.isArray(list) ? list : []);
    } catch (err) {
      toast({ title: 'Chat unavailable', description: String(err.message || err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [trade?.id]);

  // First fetch + gentle poll for new messages while the dialog is open.
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const send = useCallback(async () => {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    // Optimistic append so the user sees their message instantly.
    const optimistic = {
      id: `tmp-${Date.now()}`,
      trade_id: trade.id,
      sender_id: userId,
      sender_name: 'you',
      body,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput('');
    try {
      const saved = await api.sendTradeMessage(trade.id, body);
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? saved : m)));
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      toast({ title: 'Send failed', description: String(err.message || err), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  }, [input, sending, trade?.id, userId]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const otherName =
    trade?.sender_id === userId
      ? trade?.recipient_username || trade?.recipient_name || 'them'
      : trade?.sender_username || trade?.sender_name || 'them';

  return createPortal((
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4"
      style={{ zIndex: 100 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="trade-chat-dialog"
    >
      <div
        className="w-full max-w-md rounded-2xl border-[3px] border-[#3a1064] shadow-[0_8px_0_#3a1064,0_20px_60px_-12px_rgba(0,0,0,0.4)] overflow-hidden bg-white flex flex-col"
        style={{ maxHeight: 'calc(100vh - 24px)' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-[#3a1064] text-white">
          <div className="h-9 w-9 rounded-full bg-[#ff2bd6] border-2 border-[#ffd900] flex items-center justify-center shrink-0">
            <MessageCircle className="h-4 w-4" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[2px] text-[#ffd900]">
              Trade Chat
            </div>
            <div className="text-sm font-black leading-tight truncate">
              with <span className="text-[#ffd900]">@{otherName}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 rounded-full border-2 border-white/30 text-white hover:bg-white/10 flex items-center justify-center"
            data-testid="trade-chat-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#faf6ff]"
          style={{ minHeight: 260 }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-full text-[#3a1064]/70 font-bold gap-2 py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading chat...
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 px-4 text-[#3a1064]/70 font-bold text-sm">
              No messages yet. Say hi, confirm the platform, or offer a new
              time — messages here are private between you two.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === userId;
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                  data-testid={mine ? 'trade-chat-msg-mine' : 'trade-chat-msg-theirs'}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-snug border-2 ${
                      mine
                        ? 'bg-[#ff2bd6] text-white border-[#3a1064] rounded-br-md'
                        : 'bg-white text-[#3a1064] border-[#3a1064]/40 rounded-bl-md'
                    } ${m._optimistic ? 'opacity-70' : ''}`}
                  >
                    {!mine && (
                      <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-0.5">
                        @{m.sender_name}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`text-[9px] mt-1 font-bold ${mine ? 'text-white/70 text-right' : 'text-[#3a1064]/50'}`}>
                      {formatWhen(m.created_at)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Composer */}
        <div className="flex items-end gap-2 p-3 border-t-2 border-[#3a1064]/20 bg-white">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type a message..."
            rows={1}
            data-testid="trade-chat-input"
            className="flex-1 resize-none rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-bold text-[#3a1064] placeholder:text-[#3a1064]/40 max-h-24 min-h-[40px] bg-[#fff7e0]"
            maxLength={1000}
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !input.trim()}
            data-testid="trade-chat-send"
            className="h-10 px-3 rounded-xl bg-[#ff2bd6] text-white font-black border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] disabled:opacity-50 hover:brightness-110 active:translate-y-[1px] flex items-center gap-1.5"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={3} />}
            <span className="hidden sm:inline text-xs uppercase tracking-wider">Send</span>
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
      ' · ' +
      d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}
